import { rateGuarantee } from "./sharedTypedAmount";

import type {
  TypedAmountSpec,
  TypedAmountTypeOption,
} from "../typedAmountTypes";

const PERCENTAGE_OF_EE_BENEFIT = "percentageOfEeBenefit";
const PERCENTAGE_OF_EE_BENEFIT_AND_DOLLAR_AMOUNT =
  "percentageOfEeBenefitAndDollarAmount";
const DOLLAR_AMOUNT_LABEL = "Dollar Amount";

const NOT_INCLUDED: TypedAmountTypeOption = {
  value: "notIncluded",
  label: "Not Included",
  hideAmount: true,
};
const NOT_DISCLOSED: TypedAmountTypeOption = {
  value: "notDisclosed",
  label: "Not Disclosed",
  hideAmount: true,
};
const NOT_OFFERED: TypedAmountTypeOption = {
  value: "notOffered",
  label: "Not Offered",
  hideAmount: true,
};

const ILLNESS_PERCENTAGE_OPTIONS: TypedAmountTypeOption[] = [
  { value: "percentage", label: "Percentage" },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

const GUARANTEE_ISSUE_OPTIONS: TypedAmountTypeOption[] = [
  { value: "flatAmount", label: "Flat Amount" },
  {
    value: PERCENTAGE_OF_EE_BENEFIT,
    label: "Percentage of EE Benefit",
    amountMax: 100,
  },
  NOT_INCLUDED,
  NOT_DISCLOSED,
];

const OCCURRENCE_OPTIONS: TypedAmountTypeOption[] = [
  { value: "months", label: "Months" },
  { value: "included", label: "Included", hideAmount: true },
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];

// Benefit Maximum pairs a dollar amount (the primary amount) with a percentage
// (an extra input); the type governs which show. `percentageOfEeBenefit` hides
// the dollar amount and shows only the percentage.
const BENEFIT_MAXIMUM_PERCENT_TYPES = [
  PERCENTAGE_OF_EE_BENEFIT,
  PERCENTAGE_OF_EE_BENEFIT_AND_DOLLAR_AMOUNT,
];

const BENEFIT_MAXIMUM_OPTIONS: TypedAmountTypeOption[] = [
  { value: "dollarAmount", label: DOLLAR_AMOUNT_LABEL },
  {
    value: PERCENTAGE_OF_EE_BENEFIT,
    label: "Percentage of EE Benefit",
    hideAmount: true,
  },
  {
    value: PERCENTAGE_OF_EE_BENEFIT_AND_DOLLAR_AMOUNT,
    label: "Percentage of EE Benefit + Dollar Amount",
  },
];

// Skin Cancer, Advanced Parkinson's, and ALS (CD-2325 extended fields) share
// this exact type list — a percentage or dollar amount, or the usual
// sentinels. Product decision relayed by the user in CD-2325.
const CONDITION_RIDER_OPTIONS: TypedAmountTypeOption[] = [
  { value: "percentage", label: "Percentage" },
  { value: "dollarAmount", label: DOLLAR_AMOUNT_LABEL },
  NOT_INCLUDED,
  NOT_DISCLOSED,
  NOT_OFFERED,
];

export const criticalIllnessTypedAmountFields: Record<string, TypedAmountSpec> =
  {
    employeeGuaranteeIssue: {
      typeKey: "employeeGuaranteeIssueType",
      amountKey: "employeeGuaranteeIssueAmount",
      amountLabel: "Amount",
      typeOptions: GUARANTEE_ISSUE_OPTIONS,
    },
    spouseGuaranteeIssue: {
      typeKey: "spouseGuaranteeIssueType",
      amountKey: "spouseGuaranteeIssueAmount",
      amountLabel: "Amount",
      typeOptions: GUARANTEE_ISSUE_OPTIONS,
    },
    employeeBenefitMaximum: {
      typeKey: "employeeBenefitMaximumType",
      amountKey: "employeeBenefitMaximumDollarAmount",
      amountLabel: DOLLAR_AMOUNT_LABEL,
      amountInteger: true,
      typeOptions: [...BENEFIT_MAXIMUM_OPTIONS, NOT_DISCLOSED],
      extraKeys: [
        {
          key: "employeeBenefitMaximumPercent",
          label: "Percentage",
          showForTypes: BENEFIT_MAXIMUM_PERCENT_TYPES,
          max: 100,
        },
      ],
    },
    spouseBenefitMaximum: {
      typeKey: "spouseBenefitMaximumType",
      amountKey: "spouseBenefitMaximumDollarAmount",
      amountLabel: DOLLAR_AMOUNT_LABEL,
      amountInteger: true,
      typeOptions: [...BENEFIT_MAXIMUM_OPTIONS, NOT_INCLUDED, NOT_DISCLOSED],
      extraKeys: [
        {
          key: "spouseBenefitMaximumPercent",
          label: "Percentage",
          showForTypes: BENEFIT_MAXIMUM_PERCENT_TYPES,
          max: 100,
        },
      ],
    },
    invasiveCancer: {
      typeKey: "invasiveCancerType",
      amountKey: "invasiveCancerAmount",
      amountLabel: "Percentage",
      typeOptions: ILLNESS_PERCENTAGE_OPTIONS,
    },
    nonInvasiveCancer: {
      typeKey: "nonInvasiveCancerType",
      amountKey: "nonInvasiveCancerAmount",
      amountLabel: "Percentage",
      typeOptions: ILLNESS_PERCENTAGE_OPTIONS,
    },
    category1HeartAttack: {
      typeKey: "category1HeartAttackType",
      amountKey: "category1HeartAttackAmount",
      amountLabel: "Percentage",
      typeOptions: ILLNESS_PERCENTAGE_OPTIONS,
    },
    category1Stroke: {
      typeKey: "category1StrokeType",
      amountKey: "category1StrokeAmount",
      amountLabel: "Percentage",
      typeOptions: ILLNESS_PERCENTAGE_OPTIONS,
    },
    kidneyFailure: {
      typeKey: "kidneyFailureType",
      amountKey: "kidneyFailureAmount",
      amountLabel: "Percentage",
      typeOptions: ILLNESS_PERCENTAGE_OPTIONS,
    },
    maximumPayout: {
      typeKey: "maximumPayoutType",
      amountKey: "maximumPayoutAmount",
      amountLabel: "Amount",
      typeOptions: [
        { value: "dollarAmount", label: "Dollar Amount" },
        { value: "percentage", label: "Percentage" },
        { value: "noLimit", label: "No Limit", hideAmount: true },
        { value: "unlimited", label: "Unlimited", hideAmount: true },
        {
          value: "willNotPayForThirdOrLaterOccurrence",
          label: "Will not pay for 3rd+ occurrence",
          hideAmount: true,
        },
        NOT_INCLUDED,
        NOT_DISCLOSED,
        NOT_OFFERED,
      ],
    },
    wellnessBenefit: {
      typeKey: "wellnessBenefitType",
      amountKey: "wellnessBenefitAmount",
      amountLabel: "Amount",
      typeOptions: [
        { value: "dollarAmount", label: "Dollar amount" },
        NOT_INCLUDED,
        NOT_DISCLOSED,
        NOT_OFFERED,
      ],
    },
    occurrenceOfDifferentIllness: {
      typeKey: "occurrenceOfDifferentIllnessType",
      amountKey: "occurrenceOfDifferentIllnessMonths",
      amountLabel: "Months",
      amountInteger: true,
      typeOptions: OCCURRENCE_OPTIONS,
    },
    additionalOccurrenceOfSameIllness: {
      typeKey: "additionalOccurrenceOfSameIllnessType",
      amountKey: "additionalOccurrenceOfSameIllnessMonths",
      amountLabel: "Months",
      amountInteger: true,
      typeOptions: OCCURRENCE_OPTIONS,
    },
    rateGuarantee,
    skinCancer: {
      typeKey: "skinCancerType",
      amountKey: "skinCancerAmount",
      amountLabel: "Amount",
      typeOptions: CONDITION_RIDER_OPTIONS,
    },
    advancedParkinsons: {
      typeKey: "advancedParkinsonsType",
      amountKey: "advancedParkinsonsAmount",
      amountLabel: "Amount",
      typeOptions: CONDITION_RIDER_OPTIONS,
    },
    als: {
      typeKey: "alsType",
      amountKey: "alsAmount",
      amountLabel: "Amount",
      typeOptions: CONDITION_RIDER_OPTIONS,
    },
  };
